class FlavorTrackCarousel extends HTMLElement {
  connectedCallback() {
    const targetId = this.getAttribute('for');
    this._scroller = targetId
      ? document.getElementById(targetId)
      : this.querySelector('scroll-carousel');
    if (!this._scroller) return;

    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    this._scroller.addEventListener('wheel', this._onWheel.bind(this), { passive: false, signal });
    this._scroller.addEventListener('pointerdown', this._onPointerDown.bind(this), { signal });
    this._scroller.addEventListener('pointermove', this._onPointerMove.bind(this), { signal });
    this._scroller.addEventListener('pointerup', this._onPointerUp.bind(this), { signal });
    this._scroller.addEventListener('pointercancel', this._onPointerUp.bind(this), { signal });
    this._scroller.addEventListener('pointerleave', this._onPointerUp.bind(this), { signal });
    this._scroller.addEventListener('click', this._onClick.bind(this), { capture: true, signal });
    this._scroller.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

    this._dragState = null;
    this._didDrag = false;

    this._scroller.addEventListener('control:next', this._onControlNext.bind(this), { capture: true, signal });
    this._scroller.addEventListener('control:prev', this._onControlPrev.bind(this), { capture: true, signal });

    this._initReveal();
    this._initBadge();
    this._initLabeledTicks();
  }

  _initLabeledTicks() {
    const ticks = Array.from(this._scroller.querySelectorAll('.flavor-track__tick--labeled'));
    if (!ticks.length) return;

    const measure = () => {
      const ratio =
        parseFloat(getComputedStyle(this._scroller).getPropertyValue('--flavor-track-tick-short-ratio')) || 0.35;
      const shortTick = this._scroller.querySelector(
        '.flavor-track__tick:not(.flavor-track__tick--labeled)'
      );
      const baseTickHeight = shortTick ? shortTick.getBoundingClientRect().height / ratio : 0;

      let maxLine = baseTickHeight;
      ticks.forEach((tick) => {
        const label = tick.querySelector('.flavor-track__tick-label');
        if (!label) return;
        const length = label.getBoundingClientRect().height;
        if (length > 0) {
          const line = Math.ceil(length) + 10;
          tick.style.setProperty('--ft-label-line', line + 'px');
          if (line > maxLine) maxLine = line;
        }
      });

      if (maxLine > 0) {
        this._scroller.style.setProperty('--ft-band', Math.ceil(maxLine) + 'px');
      }
    };

    measure();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }
    window.addEventListener('resize', measure, { signal: this._abortController.signal });
  }

  _onControlNext(event) {
    if (this._scrollNeighbor(1)) {
      event.stopImmediatePropagation();
    }
  }

  _onControlPrev(event) {
    if (this._scrollNeighbor(-1)) {
      event.stopImmediatePropagation();
    }
  }

  _scrollNeighbor(direction) {
    const selector = this._scroller.getAttribute('selector');
    if (!selector) return false;
    const items = Array.from(this._scroller.querySelectorAll(selector));
    if (items.length === 0) return false;

    const scrollPadding = parseFloat(getComputedStyle(this._scroller).scrollPaddingInlineStart) || 0;
    const max = this._scroller.scrollWidth - this._scroller.clientWidth;
    const current = this._scroller.scrollLeft;

    let currentIndex = 0;
    let minDist = Infinity;
    items.forEach((item, i) => {
      const target = Math.max(0, Math.min(max, item.offsetLeft - scrollPadding));
      const dist = Math.abs(current - target);
      if (dist < minDist) {
        minDist = dist;
        currentIndex = i;
      }
    });

    const newIndex = Math.max(0, Math.min(items.length - 1, currentIndex + direction));
    if (newIndex === currentIndex) return false;
    const newTarget = Math.max(0, Math.min(max, items[newIndex].offsetLeft - scrollPadding));

    this._scroller.scrollTo({ left: newTarget, behavior: 'smooth' });
    return true;
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._revealObserver?.disconnect();
  }

  _initReveal() {
    const targets = Array.from(this._scroller.querySelectorAll('.flavor-track__ticks, .flavor-track__icons'));
    if (!targets.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    this._revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 10% 0px 10%' });

    targets.forEach((el) => this._revealObserver.observe(el));
  }

  _initBadge() {
    const section = this._scroller.closest('.section') || this._scroller.closest('.shopify-section');
    if (!section) return;
    const badge = section.querySelector('[data-flavor-track-badge]');
    const introCard = this._scroller.querySelector('.flavor-track__card--intro');
    if (!badge || !introCard) return;

    this._badgeUpdate = () => {
      const introRect = introCard.getBoundingClientRect();
      const scrollerRect = this._scroller.getBoundingClientRect();
      const scrollPadding = parseFloat(getComputedStyle(this._scroller).scrollPaddingInlineStart) || 0;
      const introHidden = introRect.right <= scrollerRect.left + scrollPadding + 4;
      badge.classList.toggle('is-revealed', introHidden);
    };

    const signal = this._abortController.signal;
    this._scroller.addEventListener('scroll', this._badgeUpdate, { passive: true, signal });
    window.addEventListener('resize', this._badgeUpdate, { signal });
    this._badgeUpdate();
  }

  _onWheel(event) {
    event.preventDefault();
  }

  _onPointerDown(event) {
    if (event.button !== 0) return;
    if (event.target.closest('button, a, input, select, textarea')) return;

    this._dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this._scroller.scrollLeft,
    };
    this._didDrag = false;

    try {
      this._scroller.setPointerCapture(event.pointerId);
    } catch (e) {
      // ignore
    }
  }

  _onPointerMove(event) {
    if (!this._dragState || event.pointerId !== this._dragState.pointerId) return;

    const dx = event.clientX - this._dragState.startX;
    if (!this._didDrag && Math.abs(dx) > 4) {
      this._didDrag = true;
      this._scroller.classList.add('is-dragging');
    }

    if (this._didDrag) {
      event.preventDefault();
      this._scroller.scrollLeft = this._dragState.startScrollLeft - dx;
    }
  }

  _onPointerUp(event) {
    if (!this._dragState || event.pointerId !== this._dragState.pointerId) return;

    try {
      this._scroller.releasePointerCapture(this._dragState.pointerId);
    } catch (e) {
      // ignore
    }

    this._dragState = null;

    if (this._didDrag) {
      this._scroller.classList.remove('is-dragging');
      this._snapToNearestCard();
      requestAnimationFrame(() => {
        this._didDrag = false;
      });
    }
  }

  _snapToNearestCard() {
    const selector = this._scroller.getAttribute('selector');
    if (!selector) return;

    const cards = Array.from(this._scroller.querySelectorAll(selector));
    if (cards.length === 0) return;

    const current = this._scroller.scrollLeft;
    const max = this._scroller.scrollWidth - this._scroller.clientWidth;
    const scrollPadding = parseFloat(getComputedStyle(this._scroller).scrollPaddingInlineStart) || 0;

    let nearest = 0;
    let minDist = Infinity;

    for (const card of cards) {
      const target = Math.max(0, Math.min(max, card.offsetLeft - scrollPadding));
      const dist = Math.abs(current - target);
      if (dist < minDist) {
        minDist = dist;
        nearest = target;
      }
    }

    if (Math.abs(current - nearest) > 1) {
      this._scroller.scrollTo({ left: nearest, behavior: 'smooth' });
    }
  }

  _onClick(event) {
    if (this._didDrag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
}

if (!window.customElements.get('flavor-track-carousel')) {
  window.customElements.define('flavor-track-carousel', FlavorTrackCarousel);
}
